import { useState, useEffect } from 'react';
import axios from 'axios';

const GetUserAvatar = ({ matricule, defaultValue = '...', nicknameColorOverride }) => {
  const [userData, setUserData] = useState({
    nicknameColor: defaultValue,
    rank: defaultValue,
    firstname: defaultValue,
    lastname: defaultValue
  });

  // Applique immédiatement une couleur forcée (permet de voir le changement sans rechargement)
  useEffect(() => {
    if (nicknameColorOverride) {
      setUserData(prev => ({ ...prev, nicknameColor: nicknameColorOverride }));
    }
  }, [nicknameColorOverride]);

  // Écoute les mises à jour de couleur déclenchées ailleurs (ex: AccountSettings)
  useEffect(() => {
    const handleNicknameColorUpdated = (event) => {
      const { matricule: updatedMatricule, color } = event.detail || {};
      if (updatedMatricule === matricule && color) {
        setUserData(prev => ({ ...prev, nicknameColor: color }));
      }
    };

    window.addEventListener('nicknameColorUpdated', handleNicknameColorUpdated);
    return () => {
      window.removeEventListener('nicknameColorUpdated', handleNicknameColorUpdated);
    };
  }, [matricule]);

  useEffect(() => {
    let isMounted = true;

    const fetchUserData = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };
        const response = await axios.get(`/api/user/avatar/${matricule}`, config);
        const data = response.data;

        if (isMounted) {
          setUserData({
            nicknameColor: nicknameColorOverride || data.nicknameColor || defaultValue,
            rank: (data.rank || defaultValue).toUpperCase(),
            firstname: data.firstname || defaultValue,
            lastname: (data.lastname || defaultValue).toUpperCase()
          });
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des données utilisateur:', err);
        if (isMounted) {
          setUserData({
            nicknameColor: defaultValue,
            rank: defaultValue,
            firstname: defaultValue,
            lastname: defaultValue
          });
        }
      }
    };

    if (matricule) {
      fetchUserData();
    }

    return () => {
      isMounted = false;
    };
  }, [matricule, defaultValue, nicknameColorOverride]);

  // Récupère les initiales de manière sécurisée (nom + prénom)
  const getInitials = () => {
    const last = userData.lastname && userData.lastname !== defaultValue ? userData.lastname[0] : '';
    const first = userData.firstname && userData.firstname !== defaultValue ? userData.firstname[0] : '';
    return (last + first).toUpperCase() || '?';
  };

  // Convertit HSL en RGB
  const hslToRgb = (h, s, l) => {
    h = h / 360;
    s = s / 100;
    l = l / 100;
    
    let r, g, b;
    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }
    return { r: r * 255, g: g * 255, b: b * 255 };
  };

  // Calcule la luminosité perçue (0-1)
  const getPerceivedLuminance = (r, g, b) => {
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  };

  // Génère la couleur complémentaire (inverse) pour le texte
  const getComplementaryColor = (color) => {
    if (!color || color === defaultValue) {
      return '#fff';
    }
    
    // Format HSL : hsl(h, s%, l%)
    const hslMatch = color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
    if (hslMatch) {
      const h = parseInt(hslMatch[1]);
      const s = parseInt(hslMatch[2]);
      const l = parseInt(hslMatch[3]);
      
      // Convertir en RGB pour calculer la luminosité perçue
      const rgb = hslToRgb(h, s, l);
      const perceivedLum = getPerceivedLuminance(rgb.r, rgb.g, rgb.b);
      
      // Couleur complémentaire : hue + 180°
      const complementH = (h + 180) % 360;
      // Luminosité basée sur la luminosité PERÇUE du fond
      const complementL = perceivedLum > 0.5 ? 15 : 90;
      
      return `hsl(${complementH}, ${s}%, ${complementL}%)`;
    }
    
    // Format hex (#fff ou #ffffff)
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const fullHex = hex.length === 3 
        ? hex.split('').map(c => c + c).join('') 
        : hex;
      const r = parseInt(fullHex.substring(0, 2), 16);
      const g = parseInt(fullHex.substring(2, 4), 16);
      const b = parseInt(fullHex.substring(4, 6), 16);
      
      if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
        const perceivedLum = getPerceivedLuminance(r, g, b);
        
        // Convertir RGB vers HSL pour le hue complémentaire
        const rNorm = r / 255, gNorm = g / 255, bNorm = b / 255;
        const max = Math.max(rNorm, gNorm, bNorm);
        const min = Math.min(rNorm, gNorm, bNorm);
        
        let h = 0, s = 0;
        if (max !== min) {
          const d = max - min;
          const l = (max + min) / 2;
          s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
          switch (max) {
            case rNorm: h = ((gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0)) / 6; break;
            case gNorm: h = ((bNorm - rNorm) / d + 2) / 6; break;
            case bNorm: h = ((rNorm - gNorm) / d + 4) / 6; break;
          }
        }
        
        const complementH = (h * 360 + 180) % 360;
        const complementL = perceivedLum > 0.5 ? 15 : 90;
        
        return `hsl(${Math.round(complementH)}, ${Math.round(s * 100)}%, ${complementL}%)`;
      }
    }
    
    // Fallback : blanc
    return '#fff';
  };

  const bgColor = userData.nicknameColor !== defaultValue ? userData.nicknameColor : '#6b7280';
  const textColor = getComplementaryColor(bgColor);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <div style={{
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: bgColor,
        boxShadow: `0 0 3px 1px ${bgColor}`,
        flexShrink: 0
      }} />
      <span>{userData.rank} {userData.lastname} {userData.firstname}</span>
    </div>
  );
};

export default GetUserAvatar;